import { Link } from 'react-router-dom'
import { useETSFComplianceStatus } from '../hooks/useETSFComplianceStatus'
import { useETSFIncidents } from '../hooks/useETSFIncidents'

// Z63.3: Monthly Audit moved out -- it's its own module with its own page
// (/customs/monthly-audit), not part of the ETSF Compliance workflow. Incidents deliberately
// stays: there's no separate Incidents module/page (it's a tab inside ETSF Compliance itself,
// IncidentsTab), and facility incidents genuinely are ETSF compliance content. Only ever
// rendered on the ETSF Compliance page itself now -- see git history if a `compact` variant is
// needed again (it was dead code, unused anywhere, so removed rather than kept unreachable
// alongside this edit).
export default function ETSFComplianceStatusCard() {
  const { status, loading } = useETSFComplianceStatus()
  const { getStats } = useETSFIncidents()

  if (loading || !status) return null

  const { dailyChecklist, weeklyAudit } = status
  const incidentStats = getStats()
  const hasOpenIncidents = incidentStats.openCount > 0

  return (
    <div className="space-y-4 mb-6">
      {/* Mid-week Checklists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link
          to="/customs/etsf?tab=Mid-week%20Checklist"
          className={`flex items-start gap-3 p-4 rounded-lg border ${
            dailyChecklist.bhx.completed
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
          }`}
        >
          <div className="text-2xl">{dailyChecklist.bhx.completed ? '✅' : '🔴'}</div>
          <div className="flex-1">
            <p className={`font-semibold ${dailyChecklist.bhx.completed ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
              {dailyChecklist.bhx.completed ? 'BHX Mid-week Checklist done' : 'BHX Mid-week Checklist not completed this week'}
            </p>
            {!dailyChecklist.bhx.completed && (
              <p className="text-sm text-red-800 dark:text-red-200 mt-1">Click to complete this week's checklist</p>
            )}
          </div>
        </Link>

        <Link
          to="/customs/etsf?tab=Mid-week%20Checklist"
          className={`flex items-start gap-3 p-4 rounded-lg border ${
            dailyChecklist.lhr.completed
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
          }`}
        >
          <div className="text-2xl">{dailyChecklist.lhr.completed ? '✅' : '🔴'}</div>
          <div className="flex-1">
            <p className={`font-semibold ${dailyChecklist.lhr.completed ? 'text-green-900 dark:text-green-100' : 'text-red-900 dark:text-red-100'}`}>
              {dailyChecklist.lhr.completed ? 'LHR Mid-week Checklist done' : 'LHR Mid-week Checklist not completed this week'}
            </p>
            {!dailyChecklist.lhr.completed && (
              <p className="text-sm text-red-800 dark:text-red-200 mt-1">Click to complete this week's checklist</p>
            )}
          </div>
        </Link>
      </div>

      {/* Weekly Audit */}
      <Link
        to="/customs/etsf?tab=Weekly%20Audit"
        className={`flex items-start gap-3 p-4 rounded-lg border ${
          weeklyAudit.daysSince === null
            ? 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
            : weeklyAudit.daysSince > 10
              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              : weeklyAudit.daysSince > 7
                ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
        }`}
      >
        <div className="text-2xl">
          {weeklyAudit.daysSince === null ? '⚪' : weeklyAudit.daysSince > 10 ? '🔴' : weeklyAudit.daysSince > 7 ? '⚠️' : '✅'}
        </div>
        <div className="flex-1">
          <p
            className={`font-semibold ${
              weeklyAudit.daysSince === null
                ? 'text-gray-900 dark:text-gray-100'
                : weeklyAudit.daysSince > 10
                  ? 'text-red-900 dark:text-red-100'
                  : weeklyAudit.daysSince > 7
                    ? 'text-amber-900 dark:text-amber-100'
                    : 'text-green-900 dark:text-green-100'
            }`}
          >
            {weeklyAudit.daysSince === null
              ? 'No weekly audit yet'
              : weeklyAudit.daysSince > 10
                ? `Weekly audit OVERDUE — ${weeklyAudit.daysSince} days since last audit`
                : weeklyAudit.daysSince > 7
                  ? `Weekly audit due — last completed ${weeklyAudit.daysSince} days ago`
                  : `Weekly audit complete — done ${weeklyAudit.daysSince} days ago`}
          </p>
          {weeklyAudit.daysSince === null && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Click to complete first audit</p>
          )}
        </div>
      </Link>

      {/* Incidents */}
      <Link
        to="/customs/etsf?tab=Incidents"
        className={`flex items-start gap-3 p-4 rounded-lg border ${
          hasOpenIncidents
            ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
            : 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
        }`}
      >
        <div className="text-2xl">
          {hasOpenIncidents ? '🔴' : '✅'}
        </div>
        <div className="flex-1">
          <p
            className={`font-semibold ${
              hasOpenIncidents
                ? 'text-red-900 dark:text-red-100'
                : 'text-green-900 dark:text-green-100'
            }`}
          >
            {hasOpenIncidents
              ? `${incidentStats.openCount} unresolved incident${incidentStats.openCount !== 1 ? 's' : ''}`
              : 'No open incidents'
            }
          </p>
          {hasOpenIncidents && incidentStats.criticalCount > 0 && (
            <p className="text-sm text-red-800 dark:text-red-200 mt-1">
              ⚠️ {incidentStats.criticalCount} critical incident{incidentStats.criticalCount !== 1 ? 's' : ''} requiring immediate attention
            </p>
          )}
        </div>
      </Link>
    </div>
  )
}
